import type { ReactNode } from "react";
import { useCmsPage } from "@/hooks/useCmsPage";
import Seo from "@/components/seo/Seo";
import PageRenderer from "@/components/cms/PageRenderer";
import type { SeoData } from "@/services/pages";

interface CmsPageProps {
  slug: string;
  path?: string;
  children?: ReactNode;
  fallbackSeo?: Partial<SeoData>;
}

/**
 * Public page wrapper. Loads the CMS page for `slug`; when it exists and is
 * published it injects auto/manual SEO + JSON-LD and renders the structured
 * sections. When no CMS page is configured yet it renders `children` (the
 * existing static page) with optional best-effort fallback SEO.
 */
export default function CmsPage({ slug, path, children, fallbackSeo }: CmsPageProps) {
  const { page, status } = useCmsPage(slug);

  if (status === "loading") {
    return (
      <div className="flex items-center justify-center min-h-[50vh] bg-background-50">
        <i className="ri-loader-4-line animate-spin text-2xl text-secondary-500"></i>
      </div>
    );
  }

  if (status === "ready" && page) {
    const faq = (page.sections || [])
      .filter((b) => b.type === "faq")
      .flatMap((b) => (Array.isArray(b.items) ? b.items : []))
      .filter(Boolean);
    const seo = { ...(page.seo || {}) } as Partial<SeoData> & { faq?: { q: string; a: string }[] };
    if (faq.length) seo.faq = faq as { q: string; a: string }[];

    const hasContent = Array.isArray(page.sections) && page.sections.length > 0;
    return (
      <>
        <Seo seo={seo} path={path ?? `/${slug}`} />
        {hasContent ? <PageRenderer sections={page.sections} /> : children}
      </>
    );
  }

  // No published CMS page → keep the existing static page (best-effort SEO).
  return (
    <>
      {fallbackSeo ? <Seo seo={fallbackSeo} path={path ?? `/${slug}`} /> : null}
      {children}
    </>
  );
}